"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface SalesAnalyticsProps {
  className?: string;
}

export default function SalesAnalytics({ className = "" }: SalesAnalyticsProps) {
  const [salesData, setSalesData] = useState("");
  const [timeRange, setTimeRange] = useState("ÚLTIMOS_30_DIAS");
  const [metrics, setMetrics] = useState("COMPLETAS");
  const [industry, setIndustry] = useState("E-COMMERCE");
  const [goals, setGoals] = useState("OTIMIZAÇÃO_DE_CONVERSÃO");
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateAnalytics = async () => {
    if (!salesData) {
      addToast("⚠️ Insira os dados de vendas", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/sales-analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          salesData: JSON.parse(salesData), 
          timeRange, 
          metrics, 
          industry, 
          goals 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        addToast("📊 Análise de vendas gerada com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro na análise de dados", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      // Se falhar ao parsear, usa dados mockados
      const mockData = generateMockAnalytics();
      setAnalytics(mockData);
      addToast("📊 Análise simulada gerada com sucesso!", "success");
    } finally {
      setIsLoading(false);
    }
  };

  const generateMockAnalytics = () => {
    return {
      executive_summary: {
        total_revenue: "R$ 125.450,00",
        growth_rate: "+23.5%",
        conversion_rate: "3.2%",
        average_ticket: "R$ 450,00",
        profit_margin: "28.5%"
      },
      top_performers: {
        products: [
          { name: "Curso Marketing Digital", sales: 156, revenue: "R$ 70.200,00" },
          { name: "Kit Ferramentas IA", sales: 98, revenue: "R$ 44.100,00" },
          { name: "Mentoria Premium", sales: 45, revenue: "R$ 11.250,00" }
        ],
        categories: [
          { name: "Cursos Online", percentage: 56 },
          { name: "Ferramentas", percentage: 35 },
          { name: "Serviços", percentage: 9 }
        ],
        sales_channels: [
          { channel: "Instagram Ads", revenue: "R$ 45.200,00", conversion: 3.8 },
          { channel: "Google Ads", revenue: "R$ 38.100,00", conversion: 2.9 },
          { channel: "Email Marketing", revenue: "R$ 28.500,00", conversion: 4.2 },
          { channel: "Orgânico", revenue: "R$ 13.650,00", conversion: 2.1 }
        ]
      },
      customer_insights: {
        total_customers: 287,
        new_customers: 89,
        returning_customers: 198,
        vip_customers: 34,
        churn_rate: 5.2,
        ltv: "R$ 437,00"
      },
      time_analysis: {
        best_day: "sexta-feira",
        best_hour: "19:00",
        peak_periods: ["18:00-22:00", "12:00-14:00"],
        seasonal_trends: "Alta em novembro/dezembro (Black Friday)"
      },
      predictive_analytics: {
        next_30_days_forecast: "R$ 155.000,00",
        growth_opportunities: ["Expandir para WhatsApp", "Criar programa de afiliados"],
        risk_factors: ["Sazonalidade em janeiro", "Aumento de CPM"],
        inventory_recommendations: ["Aumentar estoque de cursos", "Preparar para Black Friday"]
      },
      optimization_opportunities: [
        { area: "Checkout", potential_increase: "15%", action: "Implementar one-click buy" },
        { area: "Email Marketing", potential_increase: "25%", action: "Segmentação avançada" },
        { area: "Retargeting", potential_increase: "18%", action: "Anúncios dinâmicos" }
      ],
      actionable_insights: [
        "Foco em Instagram Ads - maior ROI com 3.8% de conversão",
        "Investir em email marketing - 4.2% de conversão com baixo custo",
        "Preparar campanha Black Friday - histórico de aumento 300%",
        "Otimizar checkout para sexta-feira - pico de vendas às 19h"
      ],
      recommendations: {
        immediate_actions: ["Aumentar orçamento Instagram", "Criar urgência nos emails"],
        short_term_goals: ["Implementar programa VIP", "Otimizar mobile"],
        long_term_strategy: "Expansão para WhatsApp Business e marketplace"
      }
    };
  };

  const copyInsights = (insights: string) => {
    navigator.clipboard.writeText(insights);
    addToast("📋 Insights copiados!", "success");
  };

  const loadSampleData = () => {
    const sampleData = [
      { date: "2024-01-01", product: "Curso Marketing Digital", revenue: 1500, customers: 3, channel: "Instagram" },
      { date: "2024-01-02", product: "Kit Ferramentas IA", revenue: 897, customers: 2, channel: "Google" },
      { date: "2024-01-03", product: "Mentoria Premium", revenue: 2500, customers: 1, channel: "Email" },
      { date: "2024-01-04", product: "Curso Marketing Digital", revenue: 3000, customers: 6, channel: "Instagram" },
      { date: "2024-01-05", product: "Kit Ferramentas IA", revenue: 1794, customers: 4, channel: "Google" }
    ];
    setSalesData(JSON.stringify(sampleData, null, 2));
    addToast("📊 Dados de exemplo carregados!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900 text-white rounded-lg p-6 border border-emerald-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-emerald-300">
          📊 SALES ANALYTICS DASHBOARD 📊
        </h3>
        <div className="text-xs text-emerald-400 mb-4">
          [ANALYTICS_ENGINE] v2.0.0 | STATUS: ACTIVE | AUTH: DATA_MASTER
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="md:col-span-2">
          <label className="block text-xs text-emerald-400 mb-2">SALES_DATA (JSON):</label>
          <div className="flex gap-2">
            <textarea
              value={salesData}
              onChange={(e) => setSalesData(e.target.value)}
              className="flex-1 bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-3 py-2 rounded focus:outline-none focus:border-emerald-400 h-32 text-xs"
              placeholder='[{"date": "2024-01-01", "product": "Produto", "revenue": 1000, "customers": 2}]'
            />
            <button
              onClick={loadSampleData}
              className="bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded hover:bg-emerald-500/30 text-xs"
            >
              SAMPLE
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs text-emerald-400 mb-2">TIME_RANGE:</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="w-full bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-3 py-2 rounded focus:outline-none focus:border-emerald-400"
          >
            <option value="ÚLTIMOS_7_DIAS">LAST_7_DAYS</option>
            <option value="ÚLTIMOS_30_DIAS">LAST_30_DAYS</option>
            <option value="ÚLTIMOS_90_DIAS">LAST_90_DAYS</option>
            <option value="ÚLTIMO_ANO">LAST_YEAR</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-emerald-400 mb-2">METRICS:</label>
          <select
            value={metrics}
            onChange={(e) => setMetrics(e.target.value)}
            className="w-full bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-3 py-2 rounded focus:outline-none focus:border-emerald-400"
          >
            <option value="COMPLETAS">COMPLETE</option>
            <option value="PRINCIPAIS">KEY_METRICS</option>
            <option value="CONVERSÃO">CONVERSION_ONLY</option>
            <option value="RECEITA">REVENUE_ONLY</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-emerald-400 mb-2">INDUSTRY:</label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-3 py-2 rounded focus:outline-none focus:border-emerald-400"
          >
            <option value="E-COMMERCE">E_COMMERCE</option>
            <option value="SAAS">SAAS</option>
            <option value="EDTECH">EDTECH</option>
            <option value="SERVIÇOS">SERVICES</option>
            <option value="INFOPRODUTOS">INFO_PRODUCTS</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-emerald-400 mb-2">GOALS:</label>
          <select
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            className="w-full bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-3 py-2 rounded focus:outline-none focus:border-emerald-400"
          >
            <option value="OTIMIZAÇÃO_DE_CONVERSÃO">CONVERSION_OPTIMIZATION</option>
            <option value="AUMENTO_DE_RECEITA">REVENUE_GROWTH</option>
            <option value="REDUÇÃO_DE_CUSTOS">COST_REDUCTION</option>
            <option value="RETENÇÃO">CUSTOMER_RETENTION</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateAnalytics}
        disabled={isLoading || !salesData}
        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> ANALYZING_DATA...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            📊 [ANALYZE_SALES] 📊
          </span>
        )}
      </button>

      {analytics && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-emerald-400 mb-4">
            [ANALYSIS_COMPLETE] | DATA_POINTS: {analytics.metadata?.dataPoints} | CONFIDENCE: {analytics.metadata?.confidence}
          </div>
          
          {/* Executive Summary */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">📈 EXECUTIVE_SUMMARY</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.executive_summary?.total_revenue}</div>
                <div className="text-emerald-400 text-xs">Total Revenue</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold text-green-400">{analytics.executive_summary?.growth_rate}</div>
                <div className="text-emerald-400 text-xs">Growth Rate</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.executive_summary?.conversion_rate}</div>
                <div className="text-emerald-400 text-xs">Conversion Rate</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.executive_summary?.average_ticket}</div>
                <div className="text-emerald-400 text-xs">Avg Ticket</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.executive_summary?.profit_margin}</div>
                <div className="text-emerald-400 text-xs">Profit Margin</div>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">🏆 TOP_PERFORMERS</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">BEST_SELLING_PRODUCTS:</h5>
              <div className="space-y-1">
                {analytics.top_performers?.products?.map((product: any, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded flex justify-between">
                    <span className="text-emerald-300">{product.name}</span>
                    <span className="text-emerald-400 text-xs">{product.sales} vendas</span>
                    <span className="text-emerald-300 font-bold">{product.revenue}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">SALES_CHANNELS:</h5>
              <div className="space-y-1">
                {analytics.top_performers?.sales_channels?.map((channel: any, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded flex justify-between">
                    <span className="text-emerald-300">{channel.channel}</span>
                    <span className="text-emerald-300">{channel.revenue}</span>
                    <span className="text-emerald-400 text-xs">{channel.conversion}% conv</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Customer Insights */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">👥 CUSTOMER_INSIGHTS</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.customer_insights?.total_customers}</div>
                <div className="text-emerald-400 text-xs">Total Customers</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold text-green-400">+{analytics.customer_insights?.new_customers}</div>
                <div className="text-emerald-400 text-xs">New Customers</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.customer_insights?.vip_customers}</div>
                <div className="text-emerald-400 text-xs">VIP Customers</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.customer_insights?.ltv}</div>
                <div className="text-emerald-400 text-xs">Lifetime Value</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.customer_insights?.churn_rate}%</div>
                <div className="text-emerald-400 text-xs">Churn Rate</div>
              </div>
              <div className="bg-emerald-900/30 p-2 rounded text-center">
                <div className="text-emerald-300 font-bold">{analytics.customer_insights?.returning_customers}</div>
                <div className="text-emerald-400 text-xs">Returning</div>
              </div>
            </div>
          </div>

          {/* Time Analysis */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">⏰ TIME_ANALYSIS</h4>
            <div className="bg-black/30 p-2 rounded">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <span className="text-emerald-400 text-xs">Best Day:</span>
                  <div className="text-emerald-300 font-bold">{analytics.time_analysis?.best_day}</div>
                </div>
                <div>
                  <span className="text-emerald-400 text-xs">Best Hour:</span>
                  <div className="text-emerald-300 font-bold">{analytics.time_analysis?.best_hour}</div>
                </div>
                <div>
                  <span className="text-emerald-400 text-xs">Peak Periods:</span>
                  <div className="text-emerald-300 text-sm">{analytics.time_analysis?.peak_periods?.join(", ")}</div>
                </div>
                <div>
                  <span className="text-emerald-400 text-xs">Seasonal Trend:</span>
                  <div className="text-emerald-300 text-sm">{analytics.time_analysis?.seasonal_trends}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Predictive Analytics */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">🔮 PREDICTIVE_ANALYTICS</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">30_DAYS_FORECAST:</h5>
              <div className="text-emerald-300 text-2xl font-bold bg-black/30 p-2 rounded text-center">
                {analytics.predictive_analytics?.next_30_days_forecast}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">GROWTH_OPPORTUNITIES:</h5>
              <div className="space-y-1">
                {analytics.predictive_analytics?.growth_opportunities?.map((opp: string, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded">
                    <div className="text-emerald-300 text-sm">🚀 {opp}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">RISK_FACTORS:</h5>
              <div className="space-y-1">
                {analytics.predictive_analytics?.risk_factors?.map((risk: string, index: number) => (
                  <div key={index} className="bg-black/30 p-2 rounded">
                    <div className="text-emerald-300 text-sm">⚠️ {risk}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Optimization Opportunities */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">🎯 OPTIMIZATION_OPPORTUNITIES</h4>
            <div className="space-y-2">
              {analytics.optimization_opportunities?.map((opt: any, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300 font-bold">{opt.area}</span>
                    <span className="text-emerald-400 text-sm">+{opt.potential_increase}</span>
                  </div>
                  <div className="text-emerald-300 text-sm">{opt.action}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Actionable Insights */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-emerald-300">💡 ACTIONABLE_INSIGHTS</h4>
              <button
                onClick={() => copyInsights(analytics.actionable_insights?.join("\n"))}
                className="text-xs bg-emerald-500/20 px-2 py-1 rounded hover:bg-emerald-500/30"
              >
                [COPY_INSIGHTS]
              </button>
            </div>
            <div className="space-y-1">
              {analytics.actionable_insights?.map((insight: string, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-emerald-300 text-sm">{index + 1}. {insight}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded p-4">
            <h4 className="text-lg font-bold text-emerald-300 mb-3">📋 STRATEGIC_RECOMMENDATIONS</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">IMMEDIATE_ACTIONS:</h5>
              <div className="flex flex-wrap gap-1">
                {analytics.recommendations?.immediate_actions?.map((action: string, index: number) => (
                  <span key={index} className="bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded text-xs">
                    {action}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">SHORT_TERM_GOALS:</h5>
              <div className="flex flex-wrap gap-1">
                {analytics.recommendations?.short_term_goals?.map((goal: string, index: number) => (
                  <span key={index} className="bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded text-xs">
                    {goal}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-emerald-400 mb-2">LONG_TERM_STRATEGY:</h5>
              <div className="text-emerald-300 bg-black/30 p-2 rounded">{analytics.recommendations?.long_term_strategy}</div>
            </div>
          </div>

          <div className="text-xs text-emerald-400 mt-4">
            [ANALYSIS_COMPLETE] | INDUSTRY: {industry} | TIME_RANGE: {timeRange} | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
